import React, { useEffect, useRef, useState } from "react";
import { HOMEPAGE_SCREENSHOTS_LIST } from "../../constants/homepageScreenshots";

// Scroll speed (px/sec). A fixed animation *duration* moved fewer px/sec on
// mobile (smaller cards → narrower track → looked "barely moving"), so we
// derive the duration from the measured width. Mobile gets a higher speed so
// the row feels lively on a small screen; desktop keeps its existing pace.
const PX_PER_SEC_MOBILE = 60;
const PX_PER_SEC_DESKTOP = 34;

/**
 * AppGallerySection — "Inside the app" horizontal scrolling marquee of every
 * curated screenshot in the bank. Two CSS-animated rows scroll in opposite
 * directions, each duplicated so the loop is seamless.
 *
 * Pulls from constants/homepageScreenshots.js — all images are public GCS
 * URLs from the marketing_screenshots collection.
 */

// Split the list into two rows for the two opposing marquees
const HALF = Math.ceil(HOMEPAGE_SCREENSHOTS_LIST.length / 2);
const ROW_A = HOMEPAGE_SCREENSHOTS_LIST.slice(0, HALF);
const ROW_B = HOMEPAGE_SCREENSHOTS_LIST.slice(HALF);

function PhoneCard({ screenshot }) {
  return (
    <div className="group relative w-[180px] flex-shrink-0 sm:w-[220px] md:w-[240px]">
      <div className="relative aspect-[9/19.5] overflow-hidden rounded-[1.75rem] border-[8px] border-neutral-900 bg-neutral-900 shadow-[0_15px_40px_-10px_rgba(0,0,0,0.45)] transition-transform duration-300 group-hover:-translate-y-1 group-hover:shadow-[0_25px_55px_-12px_rgba(0,0,0,0.55)]">
        <img
          src={screenshot.url}
          alt={screenshot.label}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
      </div>
      <p className="mt-3 text-center text-xs font-runcards-bold uppercase tracking-[0.14em] text-white/55 transition-colors group-hover:text-white">
        {screenshot.label}
      </p>
    </div>
  );
}

function MarqueeRow({ items, reverse = false }) {
  // Duplicate the list so the keyframe animation can loop without visible jump
  const doubled = [...items, ...items];
  const trackRef = useRef(null);
  const [durationS, setDurationS] = useState(60);

  // Measure the track and set the duration for a constant px/sec. Re-measures
  // on resize and as lazy images finish loading (which changes the width).
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    const measure = () => {
      const half = el.scrollWidth / 2; // the keyframe travels exactly one set
      const pxPerSec =
        typeof window !== "undefined" && window.innerWidth < 640
          ? PX_PER_SEC_MOBILE
          : PX_PER_SEC_DESKTOP;
      if (half > 0) setDurationS(Math.max(12, half / pxPerSec));
    };
    measure();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    el.querySelectorAll("img").forEach((img) => {
      if (!img.complete) img.addEventListener("load", measure, { once: true });
    });
    return () => { if (ro) ro.disconnect(); };
  }, []);

  return (
    <div className="relative overflow-hidden">
      {/* Edge fade so cards dissolve at left/right edges instead of clipping */}
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-[#0a0a0a] to-transparent sm:w-24" />
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-[#0a0a0a] to-transparent sm:w-24" />
      <div
        ref={trackRef}
        className="flex w-max gap-6 sm:gap-8"
        style={{
          animation: `${reverse ? "rc-marquee-reverse" : "rc-marquee"} ${durationS}s linear infinite`,
        }}
      >
        {doubled.map((s, i) => (
          <PhoneCard key={`${s.id}-${i}`} screenshot={s} />
        ))}
      </div>
    </div>
  );
}

const AppGallerySection = () => {
  return (
    <section className="relative overflow-hidden bg-[#0a0a0a] text-white">
      {/* Keyframes injected once per section render. Cheap; no CSS file plumbing. */}
      <style>{`
        @keyframes rc-marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes rc-marquee-reverse {
          0%   { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
      `}</style>

      <div className="mx-auto max-w-[1400px] px-5 pt-16 pb-10 sm:px-8 sm:pt-20 sm:pb-12 md:px-12 md:pt-24 md:pb-16">
        <div className="mb-10 max-w-2xl md:mb-14">
          <p className="mb-3 font-runcards-bold text-[11px] uppercase tracking-[0.32em] text-runcards-accent">
            Inside the app
          </p>
          <h2 className="font-runcards-black text-[clamp(2rem,5vw,3.5rem)] leading-[1.05] tracking-[-0.025em]">
            Every run. Every club.
            <br />
            <span className="text-white/70">All in one map.</span>
          </h2>
        </div>
      </div>

      {/* Marquee rows — full-bleed for maximum impact */}
      <div className="flex flex-col gap-10 pb-20 sm:gap-12 sm:pb-24">
        <MarqueeRow items={ROW_A} />
        <MarqueeRow items={ROW_B} reverse />
      </div>
    </section>
  );
};

export default AppGallerySection;
